import { useMemo, type ReactNode } from 'react'

import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import SymbolCell from '@/components/ui/SymbolCell'
import TermTip from '@/components/ui/TermTip'
import { stockLinkOptions } from '@/components/ui/symbol-link'
import MetricFigure from '@/features/screener/components/MetricFigure'
import type { ColumnSet } from '@/features/screener/lib/column-sets'
import type { Metric, ScreenerRow, ScreenSort } from '@/schemas/screener'

/**
 * Ticker over name with the logo. When the host opens stocks itself, the cell is the row's
 * keyboard stop: a button handing it the symbol (a linked row's own link does that job otherwise).
 */
const companyColumn = (
  onOpenSymbol: ((symbol: string) => void) | undefined,
): DataTableColumn<ScreenerRow> => ({
  id: 'company',
  accessorFn: (row) => row.symbol,
  header: 'Company',
  // Not a sort mm-be would honour as a column: the API's own default is by symbol (R7).
  enableSorting: false,
  meta: { fixedWidth: { base: 216, min: 136 } },
  cell: ({ row }) => {
    const cell = (
      <SymbolCell
        symbol={row.original.symbol}
        name={row.original.name}
        monogram={row.original.symbol.slice(0, 2)}
        reserveMark
      />
    )
    return onOpenSymbol === undefined ? (
      cell
    ) : (
      <button
        type="button"
        className="block w-full cursor-pointer rounded-xs text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        onClick={(event) => {
          event.stopPropagation()
          onOpenSymbol(row.original.symbol)
        }}
      >
        {cell}
      </button>
    )
  },
})

const sectorColumn: DataTableColumn<ScreenerRow> = {
  id: 'sector',
  accessorFn: (row) => row.sectorName ?? '',
  header: 'Sector',
  enableSorting: false,
  meta: { secondary: true },
  cell: ({ row }) => row.original.sectorName ?? <span className="text-muted-foreground">—</span>,
}

/** Desc first, then asc, then off (mm-be's order, by symbol): the mock's `gy` cycle. */
const metricColumn = (metric: Metric): DataTableColumn<ScreenerRow> => ({
  id: metric.key,
  accessorFn: (row) => row.values[metric.key] ?? null,
  header: () => (
    <TermTip title={metric.label} body={metric.description} variant="band">
      {metric.label}
    </TermTip>
  ),
  sortDescFirst: true,
  meta: { numeric: true },
  cell: ({ row }) => (
    <MetricFigure metric={metric} value={row.original.values[metric.key] ?? null} />
  ),
})

interface ScreenerTableProps {
  set: ColumnSet
  rows: readonly ScreenerRow[]
  sort: ScreenSort | null
  onSortChange: (sort: ScreenSort | null) => void
  /** A read is in flight: the mock's skeleton rows (`Ny`) stand in for the page. */
  running: boolean
  pageSize: number
  empty: ReactNode
  /** The host opens stocks itself: the row hands it the symbol instead of linking. */
  onOpenSymbol?: ((symbol: string) => void) | undefined
}

/**
 * The matching stocks on one page, in the chosen set's columns: Company, then the set's metrics
 * (each head a term card with mm-be's own description), then Sector on Overview. Sorting is
 * mm-be's: a head click asks for the page again in the new order. The whole row opens the stock.
 */
export default function ScreenerTable({
  set,
  rows,
  sort,
  onSortChange,
  running,
  pageSize,
  empty,
  onOpenSymbol,
}: ScreenerTableProps) {
  const columns = useMemo(
    () => [
      companyColumn(onOpenSymbol),
      ...set.metrics.map(metricColumn),
      ...(set.withSector ? [sectorColumn] : []),
    ],
    [set, onOpenSymbol],
  )

  return (
    <DataTable
      columns={columns}
      rows={running ? [] : rows}
      getRowId={(row) => row.symbol}
      caption={`Stocks matching the screen: ${set.label}`}
      sortBy={sort?.key}
      sortDesc={sort?.desc}
      onSortChange={(key, desc) => {
        onSortChange(key === undefined ? null : { key, desc })
      }}
      {...(onOpenSymbol === undefined
        ? { rowLinkOptions: (row: ScreenerRow) => stockLinkOptions(row.symbol) }
        : {
            washRows: true,
            rowProps: (row: ScreenerRow) => ({
              className: 'cursor-pointer',
              onClick: () => {
                onOpenSymbol(row.symbol)
              },
            }),
          })}
      isLoading={running}
      pageSize={pageSize}
      rowHeight="lg"
      washTone="wash"
      emptyState={empty}
    />
  )
}
